import { UpdateBlueprintPayload } from './types';
import useAxiosMutation from 'hooks/useAxiosMutation';
import { useQueryClient } from 'react-query';
import {
  BLUEPRINTS_BASE_KEY,
  BLUEPRINT_DETAILS_CACHE_KEY,
  BLUEPRINT_POWER_PROFILE_CACHE_KEY,
} from 'services/queryKeys';
import { BlueprintFromApi } from 'types';

export const useUpdateBlueprint = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading, isError, error } = useAxiosMutation<BlueprintFromApi>({
    onSuccess: () => {
      queryClient.invalidateQueries(BLUEPRINTS_BASE_KEY);
    },
  });

  const updateBlueprint = async ({ blueprintId, data }: UpdateBlueprintPayload) => {
    const formData = new FormData();

    // Build blueprint_data object - conditionally add fields using spread operator.
    // This allows for partial updates - backend will only update fields that are present.
    // For ts_end and project_type_id we check !== undefined because null/0 are valid values.
    // power_profile_create_mode should always be sent to ensure the mode is updated correctly.
    const blueprintData = {
      ...(data.name && { name: data.name }),
      ...(data.organisation_name && { organisation_name: data.organisation_name }),
      ...(data.ts_start && { ts_start: data.ts_start }),
      ...(data.ts_end !== undefined && { ts_end: data.ts_end }),
      ...(data.project_type_id !== undefined && { project_type_id: data.project_type_id }),
      ...(data.calculations && { calculations: data.calculations }),
      // Always send power_profile_create_mode - it's always included in buildUpdatePayload
      ...(data.power_profile_create_mode !== undefined && {
        power_profile_create_mode: data.power_profile_create_mode,
      }),
    };

    // Append blueprint_data as JSON string (only if we have any data)
    if (Object.keys(blueprintData).length > 0) {
      formData.append('blueprint_data', JSON.stringify(blueprintData));
    }

    if (data.power_profile_csv) {
      formData.append('edited_power_profile', data.power_profile_csv);
    }

    const result = await mutateAsync({
      method: 'PATCH',
      path: `/blueprints/${blueprintId}/`,
      body: formData,
    });

    // Store the power_profile_create_mode we sent for cache update
    const sentPowerProfileCreateMode = data.power_profile_create_mode;
    const hasCalculationsUpdate = !!data.calculations;

    if (hasCalculationsUpdate) {
      queryClient.invalidateQueries(BLUEPRINT_DETAILS_CACHE_KEY(blueprintId));
    } else if (sentPowerProfileCreateMode !== undefined) {
      // Backend may not return the updated value immediately, so we update cache manually
      queryClient.setQueryData<BlueprintFromApi | undefined>(BLUEPRINT_DETAILS_CACHE_KEY(blueprintId), (oldData) => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          power_profile_create_mode: sentPowerProfileCreateMode,
        };
      });
    }

    queryClient.invalidateQueries(BLUEPRINT_POWER_PROFILE_CACHE_KEY(blueprintId));
    queryClient.invalidateQueries(BLUEPRINTS_BASE_KEY);
    return result;
  };

  return {
    updateBlueprint,
    isLoading,
    isError,
    error,
  };
};
